import {
  Box, DashboardModule, DashboardModuleListItem, Image, Text,
} from '@domain-group/suit-theme'

import combineAgentsAndTeamMembers from '@functions/campaign'

import useBrand from '@hooks/useBrand'
import useSelector from '@hooks/useSelector'
import useTeamMembers from '@hooks/useTeamMembers'

import type { AgencyModel, AgentModel, CampaignModel } from '@models/types'

import AgentListItem from './AgentListItem'

interface OwnerDetailsModuleProps {
  agency: Partial<AgencyModel>,
  campaign: Partial<CampaignModel>,
  loading: boolean,
}

const OwnerDetailsModule = (props: OwnerDetailsModuleProps) => {
  const { agency, campaign, loading } = props

  const { team_id, agents: agentIds = [] } = campaign

  const agents = useSelector(reduxState => reduxState.entities.agents)

  const { filteredTeamMembers } = useTeamMembers({ filters: { team_id } })
  const teamMembers = team_id ? filteredTeamMembers : []

  const filteredMembersAndAgents = combineAgentsAndTeamMembers(teamMembers, agentIds, agents)
  const hasFilteredMembersAndAgents = !!filteredMembersAndAgents.length

  const brandPayload = useBrand({
    id: agency.brand_id,
  }, {
    performHttpRequests: !!agency.brand_id,
    requestOptions: {
      include_brand_logo: true,
    },
  })
  const brandLogo = brandPayload?.brand?.logo?.small || ''

  if (!agency.id || !campaign.id){
    return (
      <DashboardModule
        loading={loading}
        title="Owner Details"
      >
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>Owner Unavailable</DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            Please refresh the page or contact support.
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      </DashboardModule>
    )
  }

  return (
    <DashboardModule
      loading={loading}
      contentBoxProps={{
        flexDirection: 'column',
      }}
      title="Owner Details"
    >
      <Box paddingX="large" paddingTop="large">
        <Text fontSize="xsmall" color="bodyFontLightColor">
          Organisation
        </Text>
      </Box>

      <DashboardModuleListItem
        boxProps={{
          borderBottom: '1px solid',
        }}
        imageComponent={(
          <Image
            borderRadius="5px"
            url={brandLogo}
          />
        )}
        title={agency.title}
        secondaryText={`#${agency.id}`}
      />

      {hasFilteredMembersAndAgents && (
        <Box flexDirection="column" paddingX="large" paddingTop="large" paddingBottom="medium">
          <Text fontSize="xsmall" color="bodyFontLightColor">
            Agents
          </Text>

          <Box flexDirection="column">
            {filteredMembersAndAgents.map((member) => {
              const teamAgent = Object?.values(agents).find((a: AgentModel) => a.user_id === member.user_id)

              if (!teamAgent) return null
              return (
                <AgentListItem key={member.user_id} member={member} agent={teamAgent} />
              )
            })}
          </Box>
        </Box>
      )}
    </DashboardModule>
  )
}

export default OwnerDetailsModule
